import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AuthService } from '@shared/services/auth.service';
import {
  TuiButtonModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { AsyncPipe } from '@angular/common';
import {
  TuiInputModule,
  TuiInputPasswordModule,
  TuiIslandModule,
} from '@taiga-ui/kit';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { passwordConfirmValidator } from '@shared/validators/password-confirm.validator';
import { AlertService } from '@shared/services/alert.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegistrationForm, UserInvite } from '../../../../types/auth';
import { RouteNames } from '../../../../route-names';

@Component({
  selector: 'app-user-register-form',
  standalone: true,
  imports: [
    TuiLoaderModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiInputPasswordModule,
    TuiIslandModule,
    TuiButtonModule,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
  templateUrl: './user-register-form.component.html',
  styleUrl: './user-register-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRegisterFormComponent implements OnInit {
  invite$ = new BehaviorSubject<Nullable<UserInvite>>(null);
  loading$ = new BehaviorSubject(false);
  register$ = new Subject<void>();
  form = this.fb.group<RegistrationForm>(
    {
      token: this.fb.nonNullable.control('', Validators.required),
      lastName: this.fb.nonNullable.control('', Validators.required),
      firstName: this.fb.nonNullable.control('', Validators.required),
      middleName: this.fb.nonNullable.control(''),
      password: this.fb.nonNullable.control('', Validators.required),
      passwordConfirm: this.fb.control('', Validators.required),
    },
    {
      validators: [passwordConfirmValidator()],
    },
  );

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyRef: DestroyRef,
    private readonly auth: AuthService,
    private readonly fb: FormBuilder,
    private readonly alert: AlertService,
    private readonly router: Router,
    private readonly transloco: TranslocoService,
  ) {}

  onRegisterClick() {
    this.register$
      .pipe(
        filter(() => !this.form.invalid),
        map(() => this.form.getRawValue()),
        tap(() => this.loading$.next(true)),
        switchMap(value => {
          const { passwordConfirm, ...payload } = value;
          return this.auth.register(payload).pipe(
            tap(() => {
              this.loading$.next(false);
              this.alert.showSuccess(
                this.transloco.translate('auth.successfulRegistration'),
              );
              this.router.navigate([RouteNames.LOGIN_PAGE]);
            }),
            catchError(() => {
              this.loading$.next(false);
              this.alert.showError();
              return EMPTY;
            }),
          );
        }),

        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.onRegisterClick();

    this.activatedRoute.queryParams
      .pipe(
        map(queryParams => queryParams.token as string),
        tap(token => {
          this.form.patchValue({
            token,
          });
        }),
        catchError(() => {
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
